import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, Spinner, useConfirm } from '@/components/ui';
import { ContentLoader } from '@/components/feedback/ContentLoader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { cn } from '@/lib/utils/cn';
import { routePaths } from '@/app/routes';
import { usePatients } from '../hooks/usePatients';
import { usePatientRefresh } from '../hooks/usePatientRefresh';
import { useDetachPatient } from '../hooks/useDetachPatient';
import { AttachCodeDialog } from '../components/AttachCodeDialog';
import type { DetachPatientInput, Patient } from '../types/patient.types';

const KeyIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="7.5" cy="15.5" r="4.5" />
    <path d="m10.7 12.3 9.8-9.8M17 6l3 3M14 9l3 3" />
  </svg>
);

function initials(p: Patient): string {
  return `${p.firstName[0] ?? ''}${p.lastName[0] ?? ''}`.toUpperCase() || '?';
}

/** Section label shared by the blocks of the page (same style as the dashboard sections). */
function SectionTitle({ children }: { children: string }) {
  return (
    <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-mute">{children}</h2>
  );
}

/** Avatar + name + birth date row; `trailing` fills the right-hand slot (badge or action). */
function PatientIdentity({ patient, trailing }: { patient: Patient; trailing?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary-light text-sm font-bold text-primary">
        {initials(patient)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-ink">{patient.fullName}</p>
        {patient.birthDay && (
          <p className="text-xs text-ink-mute">
            {t('patients.list.born_on', { date: patient.birthDay.toLocaleDateString('ro-RO') })}
          </p>
        )}
      </div>
      {trailing}
    </div>
  );
}

const detachButtonClass =
  'shrink-0 rounded-input px-2.5 py-1 text-xs font-semibold text-danger transition hover:bg-danger-light disabled:opacity-50';

/**
 * "Lista pacienți" — mirrors the mobile app's patient accounts screen, split
 * in three sections:
 *   1. the primary patient (account holder) with birth date and the one-time
 *      attach code (`ActCode`) that lets another person attach THIS account
 *      to theirs — see AttachCodeDialog;
 *   2. tied to it, the accounts YOU are attached to (`attachedBy`), each of
 *      which can be left (`leaveAccount` → `detachFromAccount`);
 *   3. the dependents attached to YOUR account, each removable
 *      (`detachDependent` → `patientToDetach`), plus the add-patient entry.
 * Both detach directions go through `/v2/DetachPatient` (endpoint still under
 * construction server-side).
 *
 * The list itself comes from the cached `GetPatient` query. On every entry to
 * the page `GET /v2/GetPatientRefresh` re-reads the primary patient's attach
 * code + `attachedBy` and patches them into that cache (`usePatientRefresh`);
 * if it does not answer, the cached values simply stay on screen.
 */
export function PatientsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const patients = usePatients();
  const refresh = usePatientRefresh();
  const detach = useDetachPatient();

  /** Identifier of the association whose detach is in flight — for per-row disabling. */
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [codeOpen, setCodeOpen] = useState(false);

  const primary = patients.data?.find((p) => p.isPrimary) ?? null;
  const dependents = patients.data?.filter((p) => !p.isPrimary) ?? [];

  const runDetach = (input: DetachPatientInput, key: string) => {
    setPendingKey(key);
    detach.mutate(input, { onSettled: () => setPendingKey(null) });
  };

  const leaveAccount = async (account: string) => {
    const ok = await confirm({
      title: t('patients.list.detach_title'),
      message: t('patients.list.detach_confirm', { account }),
      confirmLabel: t('patients.list.detach'),
      tone: 'danger',
    });
    if (ok) runDetach({ mode: 'leaveAccount', account }, account);
  };

  const detachDependent = async (p: Patient) => {
    const ok = await confirm({
      title: t('patients.list.detach_dependent_title'),
      message: t('patients.list.detach_dependent_confirm', { name: p.fullName }),
      confirmLabel: t('patients.list.detach_dependent'),
      tone: 'danger',
    });
    if (ok) runDetach({ mode: 'detachDependent', patientFhirId: p.fhirId }, `dep:${p.fhirId}`);
  };

  // Banner reflecting the latest detach attempt (success or mapped failure).
  const result = detach.data;
  const banner = detach.isError
    ? { ok: false, text: t('errors.network') }
    : result
      ? result.success
        ? { ok: true, text: t('patients.list.detach_success') }
        : { ok: false, text: t(`patients.list.detach_outcome.${result.outcome}`) }
      : null;

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <PageHeader title={t('patients.list.title')} subtitle={t('patients.list.subtitle')} />

      {banner && (
        <div
          role={banner.ok ? 'status' : 'alert'}
          className={cn(
            'rounded-input px-3.5 py-2.5 text-sm',
            banner.ok ? 'bg-success-light text-success' : 'bg-danger-light text-danger',
          )}
        >
          {banner.text}
        </div>
      )}

      {patients.isLoading ? (
        <ContentLoader />
      ) : patients.isError ? (
        <ErrorState description={t('common.load_error')} onRetry={() => void patients.refetch()} />
      ) : (patients.data?.length ?? 0) === 0 ? (
        <Card className="p-6 text-sm text-ink-mute">{t('patients.list.empty')}</Card>
      ) : (
        <>
          {/* 1. Primary patient — identity, birth date and the attach code. */}
          {primary && (
            <section className="flex flex-col gap-3">
              <SectionTitle>{t('patients.list.primary_title')}</SectionTitle>
              <Card className="p-4 sm:p-5">
                <PatientIdentity
                  patient={primary}
                  trailing={<Badge tone="primary">{t('patients.you')}</Badge>}
                />

                <div className="mt-4 flex items-center justify-between gap-3 border-t border-muted-200 pt-4">
                  <p className="min-w-0 text-sm text-ink-mute">{t('patients.attachCode.hint')}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setCodeOpen(true)}
                  >
                    {KeyIcon}
                    {t('patients.attachCode.cta')}
                  </Button>
                </div>

                {/* 2. Tied to the primary patient — the accounts YOU are attached to. */}
                <div className="mt-4 border-t border-muted-200 pt-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-ink">
                      {t('patients.list.attached_to_title')}
                    </h3>
                    {refresh.isRefreshing && (
                      <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-ink-mute">
                        <Spinner size={12} />
                        {t('patients.list.attached_refreshing')}
                      </span>
                    )}
                  </div>
                  {primary.attachedBy.length === 0 ? (
                    <p className="mt-2 text-sm text-ink-mute">{t('patients.list.attached_none')}</p>
                  ) : (
                    <>
                      <p className="mt-1 text-sm text-ink-mute">
                        {t('patients.list.attached_to_hint')}
                      </p>
                      <ul className="mt-2 flex flex-col divide-y divide-muted-200">
                        {primary.attachedBy.map((account) => (
                          <li
                            key={account}
                            className="flex items-center justify-between gap-3 py-2.5"
                          >
                            <span className="min-w-0 truncate text-sm text-ink">{account}</span>
                            <button
                              type="button"
                              onClick={() => void leaveAccount(account)}
                              disabled={detach.isPending}
                              className={detachButtonClass}
                            >
                              {pendingKey === account
                                ? t('patients.list.detaching')
                                : t('patients.list.detach')}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </Card>
            </section>
          )}

          {/* 3. Dependents attached to YOUR account. */}
          <section className="flex flex-col gap-3">
            <SectionTitle>{t('patients.list.dependents_title')}</SectionTitle>
            {dependents.length === 0 ? (
              <Card className="p-4 text-sm text-ink-mute sm:p-5">
                {t('patients.list.dependents_empty')}
              </Card>
            ) : (
              <Card>
                <ul className="divide-y divide-muted-200">
                  {dependents.map((p) => (
                    <li key={p.fhirId} className="px-4 py-3 sm:px-5">
                      <PatientIdentity
                        patient={p}
                        trailing={
                          <button
                            type="button"
                            onClick={() => void detachDependent(p)}
                            disabled={detach.isPending}
                            className={detachButtonClass}
                          >
                            {pendingKey === `dep:${p.fhirId}`
                              ? t('patients.list.detaching')
                              : t('patients.list.detach_dependent')}
                          </button>
                        }
                      />
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <Link
              to={routePaths.addPatient}
              className="flex items-center gap-3 rounded-card bg-surface p-4 font-semibold text-primary shadow-card hover:bg-muted-100"
            >
              <span className="grid h-8 w-8 place-items-center rounded-full border border-dashed border-primary/40 text-lg leading-none">
                +
              </span>
              {t('patients.add.cta')}
            </Link>
          </section>
        </>
      )}

      <AttachCodeDialog open={codeOpen} patient={primary} onClose={() => setCodeOpen(false)} />
    </div>
  );
}
